// condecoracoes.tsx - Módulo Isolado de Condecorações (IBIRACO)
import React, { useEffect, useState } from 'react';
import { db, auth } from './firebaseConfig';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  serverTimestamp, 
  updateDoc, 
  doc, 
  increment,
  getDocs
} from 'firebase/firestore';
import { 
  Award, 
  Plus, 
  Search, 
  Star, 
  Medal,
  ArrowLeft,
  Trophy
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ============================================================================
// 🔧 TIPOS
// ============================================================================

type Condecoracao = {
  id: string;
  nome: string;
  descricao: string;
  icone: string;
  cor: string;
  nivel: number;
  criterios: string;
  issuedBy: string;
  createdAt: any;
  userId?: string;
  concedidaEm?: any;
  cidade?: string;
};

type Usuario = {
  uid: string;
  nome: string;
  email: string;
  foto?: string;
  condecoracoes?: number;
};

// ============================================================================
// 🖥️ COMPONENTE PRINCIPAL
// ============================================================================

export default function CondecoracoesScreen() {
  const navigate = useNavigate();
  
  // Estados da cidade do admin
  const [cidadeDisplay, setCidadeDisplay] = useState<string>('');
  const [cidadeNormalizada, setCidadeNormalizada] = useState<string>('');
  
  // Estados de condecorações
  const [condecoracoes, setCondecoracoes] = useState<Condecoracao[]>([]);
  const [novaCondecoracao, setNovaCondecoracao] = useState({ 
    nome: '', 
    descricao: '', 
    icone: 'star', 
    cor: '#FFD700', 
    nivel: 3, 
    criterios: '' 
  });
  
  // Estados para conceder
  const [usuarioParaCondecorar, setUsuarioParaCondecorar] = useState('');
  const [buscandoUsuario, setBuscandoUsuario] = useState(false);
  const [usuarioEncontrado, setUsuarioEncontrado] = useState<Usuario | null>(null);
  
  const [loading, setLoading] = useState(true);

  // ============================================================================
  // 🔄 INICIALIZAÇÃO - Buscar cidade do admin logado
  // ============================================================================
  
  useEffect(() => {
    const cidadeDisplayLS = localStorage.getItem('cidadeDisplay');
    const cidadeNormalizadaLS = localStorage.getItem('cidadeNormalizada');
    
    if (cidadeDisplayLS && cidadeNormalizadaLS) {
      setCidadeDisplay(cidadeDisplayLS);
      setCidadeNormalizada(cidadeNormalizadaLS);
      setLoading(false);
    } else {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
          const email = user.email || '';
          if (email.includes('mariocampos')) {
            setCidadeDisplay('Mário Campos');
            setCidadeNormalizada('mario_campos');
          } else if (email.includes('sarzedo')) {
            setCidadeDisplay('Sarzedo');
            setCidadeNormalizada('sarzedo');
          } else {
            setCidadeDisplay('Ibirité');
            setCidadeNormalizada('ibirite');
          }
          setLoading(false);
        }
      });
      return () => unsubscribe();
    }
  }, []);

  // ============================================================================
  // 🔄 MONITORAR CONDECORAÇÕES DA CIDADE
  // ============================================================================
  
  useEffect(() => {
    if (!cidadeNormalizada) return;

    // Monitora apenas condecorações NÃO concedidas (userId == null) da cidade
    const q = query(
      collection(db, "condecoracoes"), 
      where("userId", "==", null),
      where("cidade", "==", cidadeNormalizada)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Condecoracao));
      setCondecoracoes(data);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [cidadeNormalizada]);

  // ============================================================================
  // 🎯 FUNÇÕES DE CONDECORAÇÕES
  // ============================================================================

  // Criar novo modelo de condecoração
  const criarCondecoracao = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!novaCondecoracao.nome || !novaCondecoracao.descricao) {
      return alert("Preencha nome e descrição!");
    }
    
    try {
      await addDoc(collection(db, "condecoracoes"), {
        ...novaCondecoracao,
        issuedBy: `Prefeitura de ${cidadeDisplay}`,
        createdAt: serverTimestamp(),
        userId: null, // null = modelo disponível para conceder
        cidade: cidadeNormalizada
      });
      
      setNovaCondecoracao({ 
        nome: '', 
        descricao: '', 
        icone: 'star', 
        cor: '#FFD700', 
        nivel: 3, 
        criterios: '' 
      });
      
      alert("Condecoração criada com sucesso!");
    } catch (err) {
      console.error(err);
      alert("Erro ao criar condecoração.");
    }
  };

  // Buscar usuário por UID ou email
  const buscarUsuario = async () => {
    if (!usuarioParaCondecorar) return;
    
    setBuscandoUsuario(true);
    try {
      // Tenta buscar por UID primeiro
      let userSnap = await getDocs(
        query(collection(db, "usuarios"), where("__name__", "==", usuarioParaCondecorar))
      );
      
      if (userSnap.empty) {
        // Tenta buscar por email
        userSnap = await getDocs(
          query(collection(db, "usuarios"), where("email", "==", usuarioParaCondecorar))
        );
      }
      
      if (!userSnap.empty) {
        setUsuarioEncontrado({ 
          uid: userSnap.docs[0].id, 
          ...userSnap.docs[0].data() 
        } as Usuario);
      } else {
        alert("Usuário não encontrado.");
        setUsuarioEncontrado(null);
      }
    } catch (err) {
      console.error(err);
      alert("Erro ao buscar usuário.");
    } finally {
      setBuscandoUsuario(false);
    }
  };

  // Conceder condecoração a um usuário
  const concederCondecoracao = async (condecId: string, condecNome: string) => {
    if (!usuarioEncontrado) {
      return alert("Busque um usuário primeiro!");
    }
    
    if (!window.confirm(`Conceder "${condecNome}" para ${usuarioEncontrado.nome || usuarioEncontrado.email}?`)) {
      return;
    }
    
    try {
      // 1. Atualiza a condecoração com o userId
      await updateDoc(doc(db, "condecoracoes", condecId), {
        userId: usuarioEncontrado.uid,
        concedidaEm: serverTimestamp()
      });
      
      // 2. Adiciona a condecoração ao perfil do usuário
      const userRef = doc(db, "usuarios", usuarioEncontrado.uid);
      await updateDoc(userRef, {
        condecoracoes: increment(1),
        [`condecoracoesDetalhes.${condecId}`]: {
          nome: condecNome,
          concedidaEm: new Date(),
          cidade: cidadeDisplay
        }
      });
      
      alert(`🏆 "${condecNome}" concedida com sucesso!`);
      setUsuarioParaCondecorar('');
      setUsuarioEncontrado(null);
    } catch (err) {
      console.error(err);
      alert("Erro ao conceder condecoração.");
    }
  };

  // ============================================================================
  // 🎨 RENDERIZAÇÃO
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F7FE] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#FFD700] mx-auto mb-4"></div>
          <p className="text-gray-600 font-bold">Carregando condecorações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FE] p-4 md:p-8">
      {/* HEADER */}
      <div className="max-w-5xl mx-auto mb-6">
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-[#6200EE] transition-all mb-4 font-bold"
        >
          <ArrowLeft size={20} /> Voltar para o Dashboard
        </button>
        
        <div className="bg-white p-6 rounded-[2.5rem] shadow-sm flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-[#FFD700] p-3 rounded-2xl">
              <Trophy size={24} className="text-[#1A1C1D]" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-[#1A1C1D]">Condecorações</h1>
              <p className="text-sm text-gray-500 font-bold">Prefeitura de {cidadeDisplay}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto space-y-6">
        
        {/* CRIAR NOVA CONDECORAÇÃO */}
        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100">
          <h3 className="font-black text-lg text-[#6200EE] mb-6 flex items-center gap-2">
            <Plus size={20} /> Criar Nova Condecoração
          </h3>
          
          <form onSubmit={criarCondecoracao} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-black text-gray-700 uppercase mb-2">
                  Nome da Condecoração *
                </label>
                <input 
                  value={novaCondecoracao.nome} 
                  onChange={e => setNovaCondecoracao(prev => ({ ...prev, nome: e.target.value }))} 
                  placeholder="Ex: Cidadão Exemplar" 
                  className="w-full p-3 bg-gray-50 rounded-xl font-bold text-sm outline-none border-2 border-transparent focus:border-purple-200 transition-all" 
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-black text-gray-700 uppercase mb-2">
                  Ícone
                </label>
                <select 
                  value={novaCondecoracao.icone}
                  onChange={e => setNovaCondecoracao(prev => ({ ...prev, icone: e.target.value }))}
                  className="w-full p-3 bg-gray-50 rounded-xl font-bold text-sm outline-none border-2 border-transparent focus:border-purple-200 transition-all"
                >
                  <option value="star">⭐ Estrela</option>
                  <option value="emoji-events">🏆 Troféu</option>
                  <option value="military-tech">🎖️ Medalha</option>
                  <option value="workspace-premium">💎 Diamante</option>
                  <option value="verified">✅ Verificado</option>
                  <option value="android">🤖 Android/Borg</option>
                  <option value="local-fire-department">🔥 Fogo</option>
                  <option value="shield">🛡️ Escudo</option>
                  <option value="rocket">🚀 Foguete</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-black text-gray-700 uppercase mb-2">
                Descrição *
              </label>
              <textarea 
                value={novaCondecoracao.descricao} 
                onChange={e => setNovaCondecoracao(prev => ({ ...prev, descricao: e.target.value }))} 
                placeholder="Descreva o significado desta condecoração..." 
                className="w-full p-3 bg-gray-50 rounded-xl font-bold text-sm outline-none border-2 border-transparent focus:border-purple-200 h-24 resize-none transition-all" 
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-black text-gray-700 uppercase mb-2">
                  Cor
                </label>
                <div className="flex items-center gap-2">
                  <input 
                    type="color"
                    value={novaCondecoracao.cor}
                    onChange={e => setNovaCondecoracao(prev => ({ ...prev, cor: e.target.value }))}
                    className="w-16 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <span className="text-sm font-bold text-gray-600">{novaCondecoracao.cor}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-black text-gray-700 uppercase mb-2">
                  Nível
                </label>
                <select 
                  value={novaCondecoracao.nivel}
                  onChange={e => setNovaCondecoracao(prev => ({ ...prev, nivel: Number(e.target.value) }))}
                  className="w-full p-3 bg-gray-50 rounded-xl font-bold text-sm outline-none border-2 border-transparent focus:border-purple-200 transition-all"
                >
                  <option value={1}>🥉 Bronze</option>
                  <option value={2}>🥈 Prata</option>
                  <option value={3}>🥇 Ouro</option>
                  <option value={4}>💎 Diamante</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-black text-gray-700 uppercase mb-2">
                  Critérios
                </label>
                <input 
                  value={novaCondecoracao.criterios}
                  onChange={e => setNovaCondecoracao(prev => ({ ...prev, criterios: e.target.value }))}
                  placeholder="Ex: 100 reportes"
                  className="w-full p-3 bg-gray-50 rounded-xl font-bold text-sm outline-none border-2 border-transparent focus:border-purple-200 transition-all"
                />
              </div>
            </div>

            <button 
              type="submit" 
              className="w-full bg-[#6200EE] text-white py-4 rounded-xl font-black uppercase text-xs tracking-widest shadow-lg hover:bg-[#4800b2] transition-all"
            >
              Criar Condecoração
            </button>
          </form>
        </div>

        {/* CONCEDER CONDECORAÇÃO */}
        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100">
          <h3 className="font-black text-lg text-[#6200EE] mb-6 flex items-center gap-2">
            <Medal size={20} /> Conceder Condecoração
          </h3>
          
          {/* Buscar Usuário */}
          <div className="mb-6">
            <label className="block text-sm font-black text-gray-700 uppercase mb-2">
              Buscar Cidadão (UID ou Email)
            </label>
            <div className="flex gap-2">
              <input 
                value={usuarioParaCondecorar}
                onChange={e => setUsuarioParaCondecorar(e.target.value)}
                placeholder="Digite o email ou UID do cidadão"
                className="flex-1 p-3 bg-gray-50 rounded-xl font-bold text-sm outline-none border-2 border-transparent focus:border-purple-200 transition-all"
              />
              <button 
                onClick={buscarUsuario}
                disabled={buscandoUsuario}
                className="bg-gray-100 px-6 rounded-xl font-black text-xs uppercase hover:bg-gray-200 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {buscandoUsuario ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#6200EE]"></div>
                ) : (
                  <><Search size={16} /> Buscar</>
                )}
              </button>
            </div>
          </div>

          {/* Usuário Encontrado */}
          {usuarioEncontrado && (
            <div className="bg-purple-50 p-4 rounded-xl mb-6 flex items-center gap-4 border-2 border-purple-200">
              <img 
                src={usuarioEncontrado.foto || 'https://via.placeholder.com/60'} 
                className="w-14 h-14 rounded-full border-2 border-white shadow-sm" 
                alt="User" 
              />
              <div className="flex-1">
                <p className="font-bold text-base text-gray-800">{usuarioEncontrado.nome || usuarioEncontrado.email}</p>
                <p className="text-[10px] text-gray-500 font-bold uppercase">
                  {usuarioEncontrado.email}
                </p>
                <p className="text-xs text-purple-600 font-bold mt-1">
                  Condecorações: {usuarioEncontrado.condecoracoes || 0}
                </p>
              </div>
            </div>
          )}

          {/* Lista de Condecorações Disponíveis */}
          <div>
            <h4 className="font-black text-sm text-gray-700 uppercase mb-4">
              Condecorações Disponíveis ({condecoracoes.length})
            </h4>
            
            {condecoracoes.length > 0 ? (
              <div className="space-y-3">
                {condecoracoes.map(condec => (
                  <div 
                    key={condec.id} 
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-[#6200EE] transition-all"
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center shadow-sm" 
                        style={{ backgroundColor: `${condec.cor}20` }}
                      >
                        <Star size={24} style={{ color: condec.cor }} />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-gray-800">{condec.nome}</p>
                        <p className="text-[10px] text-gray-500 font-medium">{condec.descricao}</p>
                        {condec.criterios && (
                          <p className="text-[9px] text-gray-400 font-bold uppercase mt-1">
                            {condec.criterios}
                          </p>
                        )}
                      </div>
                    </div>
                    <button 
                      onClick={() => concederCondecoracao(condec.id, condec.nome)}
                      disabled={!usuarioEncontrado}
                      className="bg-[#6200EE] text-white px-5 py-2.5 rounded-lg font-black text-[10px] uppercase hover:bg-[#4800b2] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      Conceder
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-xl">
                <Award size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="text-sm font-bold text-gray-500">
                  Nenhuma condecoração disponível
                </p>
                <p className="text-[10px] text-gray-400 mt-1">
                  Crie uma condecoração acima para começar
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}